import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* ===== DESKTOP BACKGROUND ===== */}
      <div 
        className="absolute inset-0 w-full h-full hidden lg:block"
        style={{ 
          transform: `translateY(${scrollY * 0.4}px)`,
          willChange: 'transform'
        }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/d54404dd2e06c97be604c367071623bc.jpg"
          alt="Infrastructure technique de réparation de bagages premium"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* ===== MOBILE BACKGROUND ===== */}
      <div className="absolute inset-0 w-full h-full lg:hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20collection%20of%20premium%20luxury%20suitcases%20and%20travel%20luggage%20in%20various%20sizes%20arranged%20artistically%20in%20modern%20bright%20airport%20terminal%2C%20sleek%20black%20and%20metallic%20hard%20shell%20cases%20with%20polished%20surfaces%2C%20warm%20natural%20morning%20light%20streaming%20through%20large%20glass%20windows%2C%20contemporary%20minimalist%20airport%20architecture%20with%20clean%20lines%2C%20professional%20commercial%20photography%20with%20shallow%20depth%20of%20field%2C%20teal%20and%20warm%20orange%20accent%20lighting%2C%20sophisticated%20travel%20lifestyle%20aesthetic&width=800&height=1200&seq=hero-mobile-luggage-airport-001&orientation=portrait"
          alt="Bagages premium en aéroport"
          className="w-full h-full object-cover object-center"
        />
      </div>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e]/90 via-[#16213e]/85 to-[#0f3460]/90"></div>

      {/* Subtle Animated Accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF6B6B]/10 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF8E53]/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>

      {/* Geometric Elements - Desktop only */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden lg:block">
        <div className="absolute top-32 right-20 w-4 h-4 bg-[#FF6B6B] rounded-full"></div>
        <div className="absolute top-48 right-40 w-2 h-2 bg-[#4ECDC4] rounded-full"></div>
        <div className="absolute bottom-40 left-20 w-3 h-3 bg-[#FF8E53] rounded-full"></div>
        <div className="absolute top-1/3 left-16 w-20 h-20 border border-white/10 rounded-2xl rotate-12"></div>
        <div className="absolute bottom-1/3 right-24 w-16 h-16 border border-[#FF6B6B]/20 rounded-full"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-32 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-[80vh]">
          
          {/* ===== MOBILE LAYOUT ===== */}
          <div className="lg:hidden flex flex-col items-center text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-5">
              <span className="w-2 h-2 bg-[#4ECDC4] rounded-full animate-pulse"></span>
              <span className="text-white/80 text-xs font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t('hero.badge')}
              </span>
            </div>

            {/* Title - compact for mobile */}
            <h1
              className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('hero.title')}
              <br />
              <span className="bg-gradient-to-r from-[#FF6B6B] via-[#FF8E53] to-[#4ECDC4] bg-clip-text text-transparent">
                {t('hero.titleHighlight')}
              </span>
            </h1>

            <p
              className="text-sm sm:text-base text-white/60 mb-6 max-w-md leading-relaxed"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('hero.description')}
            </p>

            {/* Image sous la description */}
            <div className="w-full max-w-md mb-6 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20luggage%20repair%20technician%20carefully%20fixing%20a%20premium%20hard%20shell%20suitcase%20in%20a%20bright%20modern%20airport%20workshop%2C%20close%20up%20of%20skilled%20hands%20using%20precision%20tools%20on%20a%20sleek%20black%20travel%20case%2C%20warm%20teal%20and%20orange%20accent%20lighting%2C%20clean%20organized%20workspace%20with%20professional%20equipment%2C%20contemporary%20industrial%20aesthetic%2C%20commercial%20photography%20with%20shallow%20depth%20of%20field%2C%20high%20end%20craftsmanship%20detail%20shot&width=800&height=450&seq=hero-mobile-inline-img-001&orientation=landscape"
                alt="Réparation professionnelle de bagages en aéroport"
                className="w-full h-[200px] sm:h-[240px] object-cover object-top"
              />
            </div>

            {/* Mini process strip */}
            <div className="w-full max-w-md grid grid-cols-3 gap-2 mb-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="w-8 h-8 mx-auto mb-1.5 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-lg flex items-center justify-center">
                  <i className="ri-camera-line text-white text-sm"></i>
                </div>
                <div className="text-white text-[10px] font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>Diagnostic</div>
                <div className="text-white/40 text-[9px]">Photo & devis</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="w-8 h-8 mx-auto mb-1.5 bg-gradient-to-br from-[#FF8E53] to-[#FFD93D] rounded-lg flex items-center justify-center">
                  <i className="ri-tools-line text-white text-sm"></i>
                </div>
                <div className="text-white text-[10px] font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>Réparation</div>
                <div className="text-white/40 text-[9px]">1-2h express</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="w-8 h-8 mx-auto mb-1.5 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-lg flex items-center justify-center">
                  <i className="ri-check-double-line text-white text-sm"></i>
                </div>
                <div className="text-white text-[10px] font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>Restitution</div>
                <div className="text-white/40 text-[9px]">Garanti 2 ans</div>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 mb-7 w-full max-w-md">
              <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="text-xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>-65%</div>
                <div className="text-white/40 text-[10px]">{t('hero.stat1')}</div>
              </div>
              <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="text-xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>1h-2h</div>
                <div className="text-white/40 text-[10px]">{t('hero.stat2')}</div>
              </div>
              <div className="flex-1 bg-white/5 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="text-xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>98%</div>
                <div className="text-white/40 text-[10px]">{t('hero.stat3')}</div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
              <Link
                to="/contact"
                className="group flex-1 px-6 py-3.5 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-[#FF6B6B]/30 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <button
                onClick={() => scrollToSection('services')}
                className="flex-1 px-6 py-3.5 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/20 text-sm"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('hero.ctaSecondary')}
              </button>
            </div>
          </div>

          {/* ===== DESKTOP LEFT CONTENT ===== */}
          <div className="hidden lg:block">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
              <span className="w-2 h-2 bg-[#4ECDC4] rounded-full animate-pulse"></span>
              <span
                className="text-white/80 text-sm font-medium"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('hero.badge')}
              </span>
            </div>

            <h1
              className="text-5xl xl:text-7xl font-bold text-white mb-8 leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('hero.title')}
              <br />
              <span className="bg-gradient-to-r from-[#FF6B6B] via-[#FF8E53] to-[#4ECDC4] bg-clip-text text-transparent">
                {t('hero.titleHighlight')}
              </span>
            </h1>

            <p
              className="text-lg xl:text-xl text-white/70 mb-10 max-w-lg leading-relaxed"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('hero.description')}
            </p>

            <div className="flex flex-row flex-wrap gap-4 mb-12">
              <Link
                to="/contact"
                className="group px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-[#FF6B6B]/30 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('hero.ctaPrimary')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </Link>
              <button
                onClick={() => scrollToSection('services')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-semibold hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/20"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('hero.ctaSecondary')}
              </button>
            </div>

            {/* Value Propositions */}
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  -65%
                </div>
                <div className="text-white/50 text-sm whitespace-nowrap">{t('hero.stat1')}</div>
              </div>
              <div className="w-px bg-white/20"></div>
              <div>
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  1h-2h
                </div>
                <div className="text-white/50 text-sm whitespace-nowrap">{t('hero.stat2')}</div>
              </div>
              <div className="w-px bg-white/20"></div>
              <div>
                <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  98%
                </div>
                <div className="text-white/50 text-sm whitespace-nowrap">{t('hero.stat3')}</div>
              </div>
            </div>
          </div>

          {/* ===== DESKTOP RIGHT VISUAL ===== */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20premium%20luggage%20repair%20workshop%20with%20expert%20technician%20hands%20carefully%20fixing%20a%20luxury%20suitcase%20wheel%20mechanism%2C%20warm%20orange%20and%20teal%20lighting%2C%20professional%20tools%20arranged%20neatly%2C%20clean%20minimalist%20workspace%20close-up%20detail%20shot%20showing%20precision%20craftsmanship%2C%20contemporary%20industrial%20aesthetic%20with%20soft%20bokeh%20background&width=600&height=700&seq=hero-main-001&orientation=portrait"
                  alt="Réparation de bagages premium"
                  className="w-full h-[500px] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e]/20 via-transparent to-transparent"></div>
              </div>

              {/* Floating Cards */}
              <div
                className="absolute -left-8 top-20 bg-white rounded-2xl p-4 shadow-xl animate-bounce z-20"
                style={{ animationDuration: '3s' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-xl flex items-center justify-center">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div
                      className="text-2xl font-bold text-gray-900"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      1h-2h
                    </div>
                    <div className="text-gray-500 text-xs">Réparation express</div>
                  </div>
                </div>
              </div>

              <div
                className="absolute -right-4 bottom-32 bg-white rounded-2xl p-4 shadow-xl animate-bounce z-20"
                style={{ animationDuration: '4s', animationDelay: '0.5s' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl flex items-center justify-center">
                    <i className="ri-shield-check-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <div
                      className="text-2xl font-bold text-gray-900"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      IATA
                    </div>
                    <div className="text-gray-500 text-xs">Conformité totale</div>
                  </div>
                </div>
              </div>

              {/* Decorative Ring */}
              <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border-2 border-dashed border-[#FF6B6B]/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}
